import { formatDateToYYYYMMDD } from "utils";

/**
 * Build a [from, to] date range ending today, based on a "last N days" selection.
 *
 * @param {number|null|undefined} days - e.g. 1, 7, 14, 28. Use 0 for "All time".
 * @param {Date} [now=new Date()] - current date.
 * @returns {{ from: string, to: string } | null} yyyy-mm-dd range, or null for "All time"
 */
export function buildLastUpdatedRange(days, now = new Date()) {
  if (days === 0) return null; // "All time"

  const n = Number(days);
  if (!Number.isFinite(n) || n <= 0) {
    throw new Error(`Invalid days value: ${days}`);
  }

  // Normalize to local start-of-day to avoid time-of-day and DST edge cases
  const end = new Date(now);
  end.setHours(0, 0, 0, 0);

  const start = new Date(end);
  start.setDate(start.getDate() - n);

  return {
    postedFrom: formatDateToYYYYMMDD(start),
    postedTo: formatDateToYYYYMMDD(end),
  };
}

/**
 * Validates the currently selected date range and returns a user-friendly error message.
 *
 * Checks, in order:
 * 1) Both "from" and "to" dates are present.
 * 2) The two dates are not the same.
 * 3) The "from" date is not after the "to" date.
 *
 * @returns {string|null}
 *   A validation error message when the selection is invalid, or `null` when the
 *   date range is valid.
 */
export const getErrorMessage = (dates) => {
  if (!dates.fromDate || !dates.toDate) {
    return "Please select both dates";
  } else if (dates.fromDate === dates.toDate) {
    return "From date cannot be the same as To date";
  } else if (new Date(dates.fromDate) > new Date(dates.toDate)) {
    return "From date cannot be after To date";
  }
  return null;
};
